'use client'

import { useEffect, useMemo, useState } from 'react'
import { Property, Application } from '@/types'
import { useCurrency } from '@/lib/currency'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useToast } from '@/hooks/useToast'

interface ApplicationFormProps {
  property: Property
  onSuccess: (application: Application) => void
  onCancel: () => void
  isOpen: boolean
}

export default function ApplicationForm({ property, onSuccess, onCancel, isOpen }: ApplicationFormProps) {
  const [loading, setLoading] = useState(false)
  const { format } = useCurrency()
  const toast = useToast()
  const isShortlet = property.rentalType === 'short-term'
  
  // Date range state for shortlets
  const [bookingDates, setBookingDates] = useState<{start: string, end: string}>({ start: '', end: '' })
  
  // Calculate total price for shortlet based on selected dates
  const calculateShortletTotal = () => {
    if (!isShortlet || !bookingDates.start || !bookingDates.end) return 0
    const start = new Date(bookingDates.start)
    const end = new Date(bookingDates.end)
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays * property.price
  }

  // RHF + Zod schema (behind feature flag)
  const schema = useMemo(() => {
    if (isShortlet) {
      return z.object({
        message: z.string().max(1000).optional().or(z.literal('')),
        monthly_income: z
          .string()
          .min(1, 'Monthly income is required')
          .refine(v => Number(v) >= 0, 'Income must be a positive number'),
        employment_status: z.string().min(1, 'Employment status is required'),
        custom_employment_status: z.string().optional(),
        booking_start_date: z.string().min(1, 'Check-in date is required'),
        booking_end_date: z.string().min(1, 'Check-out date is required'),
        kyc_docs: z.record(z.string()).optional().default({})
      })
    } else {
      return z.object({
        message: z.string().max(1000).optional().or(z.literal('')),
        monthly_income: z
          .string()
          .min(1, 'Monthly income is required')
          .refine(v => Number(v) >= 0, 'Income must be a positive number'),
        employment_status: z.string().min(1, 'Employment status is required'),
        custom_employment_status: z.string().optional(),
        move_in_date: z.string().min(1, 'Move-in date is required'),
        lease_duration: z.enum(['6', '12']).default(String(Math.min(property.duration || 12, 12)) as '6' | '12'),
        kyc_docs: z.record(z.string()).optional().default({})
      })
    }
  }, [property.duration, isShortlet])

  type FormValues = z.infer<typeof schema>

  // Type for API request body
  type ApplicationRequestBody = {
    propertyId: string
    message: string
    monthly_income: number
    employment_status: string
    kyc_docs: Record<string, string>
    status: 'pending'
    booking_start_date?: string
    booking_end_date?: string
    total_amount?: number
    move_in_date?: string
    lease_duration?: number
  }

  const {
    register,
    handleSubmit: rhfHandleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: isShortlet ? {
      message: '',
      monthly_income: '',
      employment_status: '',
      custom_employment_status: '',
      booking_start_date: '',
      booking_end_date: '',
      kyc_docs: {}
    } : {
      message: '',
      monthly_income: '',
      employment_status: '',
      custom_employment_status: '',
      move_in_date: '',
      lease_duration: (property.duration ? String(Math.min(property.duration, 12)) : '12') as '6' | '12',
      kyc_docs: {}
    }
  })

  // Draft autosave to localStorage (scoped per property) - client-side only
  useEffect(() => {
    if (typeof window === 'undefined') return
    
    const key = `application_draft_${property.id}`
    const sub = watch(values => {
      try { 
        if (typeof window !== 'undefined') {
          localStorage.setItem(key, JSON.stringify(values)) 
        }
      } catch {
        // Silently handle localStorage errors (e.g., quota exceeded, private browsing)
      }
    })
    
    // Load existing draft - only on client side
    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem(key)
        if (raw) reset(JSON.parse(raw))
      } catch {
        // Silently handle localStorage parse errors
      }
    }
    
    return () => sub.unsubscribe()
  }, [watch, reset, property.id])

  // Handle file upload for KYC documents
  const handleFileUpload = async (file: File, documentType: string) => {
    try {
      // Upload file to backend
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('documentType', documentType)

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      const response = await fetch(`${apiUrl}/upload/single`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error('Upload failed')
      }

      const data = await response.json()
      // Extract URL from response - could be data.url, data.file.url, or data.fileUrl
      const fileUrl = data.url || data.file?.url || data.fileUrl || data.path
      if (!fileUrl) {
        throw new Error('No URL returned from upload')
      }
      const current = watch('kyc_docs') || {}
      setValue('kyc_docs', { ...current, [documentType]: fileUrl }, { shouldDirty: true })

      toast.success('Document uploaded successfully!')
    } catch (error) {
      console.error('File upload error:', error)
      toast.error('Failed to upload document. Please try again.')
    }
  }

  // Submit application
  const handleSubmit = async (values: FormValues) => {
    setLoading(true)

    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        toast.error('Please log in to submit an application')
        setLoading(false)
        return
      }

      const requestBody: ApplicationRequestBody = {
        propertyId: property.id, // Use propertyId instead of property_id to match backend
        message: values.message || '',
        monthly_income: parseFloat(values.monthly_income) || 0,
        employment_status: values.employment_status === 'other' ? values.custom_employment_status || 'Other' : values.employment_status,
        kyc_docs: values.kyc_docs || {},
        status: 'pending'
      }
      
      // Add shortlet-specific or long-term-specific fields
      if (isShortlet) {
        if ('booking_start_date' in values) {
          requestBody.booking_start_date = values.booking_start_date
        }
        if ('booking_end_date' in values) {
          requestBody.booking_end_date = values.booking_end_date
        }
        requestBody.total_amount = calculateShortletTotal()
      } else {
        if ('move_in_date' in values) {
          requestBody.move_in_date = values.move_in_date
        }
        if ('lease_duration' in values) {
          requestBody.lease_duration = parseInt(values.lease_duration) || 12
        }
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      const response = await fetch(`${apiUrl}/applications`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorText = await response.text()
        let errorData
        try {
          errorData = JSON.parse(errorText)
        } catch {
          errorData = { message: errorText || 'Failed to submit application' }
        }
        console.error('Application submission error:', response.status, errorData)
        toast.error(errorData.message || 'Failed to submit application. Please try again.')
        setLoading(false)
        return
      }

      const application = await response.json()
      console.log('✅ Application submitted successfully:', application)
      toast.success('Application submitted successfully!')
      onSuccess(application.application || application)
    } catch (error) {
      console.error('Submit error:', error)
      toast.error(error instanceof Error ? error.message : 'An error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Format price via currency context
  const formatPrice = (price: number) => format(price)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Inquire About Rental</h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Property Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">{property.title}</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">{isShortlet ? 'Price per Day:' : 'Monthly Rent:'}</span>
                <span className="font-semibold text-green-600 ml-2">{formatPrice(property.price)}</span>
              </div>
              <div>
                <span className="text-gray-600">Location:</span>
                <span className="font-semibold ml-2">{property.location}</span>
              </div>
              {!isShortlet && (
                <>
                  <div>
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-semibold ml-2">{property.duration} months</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span className="font-semibold text-green-600 ml-2">✓ Verified</span>
                  </div>
                </>
              )}
              {isShortlet && (
                <>
                  <div>
                    <span className="text-gray-600">Type:</span>
                    <span className="font-semibold ml-2">Shortlet</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span className="font-semibold text-green-600 ml-2">✓ Verified</span>
                  </div>
                </>
              )}
            </div>
            {isShortlet && bookingDates.start && bookingDates.end && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Cost:</span>
                  <span className="font-bold text-lg text-green-600">
                    {formatPrice(calculateShortletTotal())}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {(() => {
                    const start = new Date(bookingDates.start)
                    const end = new Date(bookingDates.end)
                    const diffTime = Math.abs(end.getTime() - start.getTime())
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                    return `${diffDays} day${diffDays !== 1 ? 's' : ''} × ${formatPrice(property.price)}/day`
                  })()}
                </p>
              </div>
            )}
          </div>


          {/* Application Form */}
          <form onSubmit={rhfHandleSubmit(handleSubmit)} className="space-y-6">
              {/* Personal Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message to Landlord (Optional)</label>
                <textarea
                  {...register('message')}
                  placeholder="Tell the landlord about yourself, why you're interested in this property, and any questions you have..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                />
              </div>

              {/* Financial Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Income *</label>
                  <input
                    type="number"
                    {...register('monthly_income')}
                    placeholder="Enter your monthly income"
                    min={0}
                    step={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {errors.monthly_income && (
                    <p className="text-xs text-red-600 mt-1">{errors.monthly_income.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employment Status *</label>
                  <select
                    {...register('employment_status')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select employment status</option>
                    <option value="employed">Employed (Full-time)</option>
                    <option value="part_time">Part-time</option>
                    <option value="self_employed">Self-employed</option>
                    <option value="student">Student</option>
                    <option value="retired">Retired</option>
                    <option value="unemployed">Unemployed</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.employment_status && (
                    <p className="text-xs text-red-600 mt-1">{errors.employment_status.message}</p>
                  )}
                  {watch('employment_status') === 'other' && (
                    <div className="mt-2">
                      <input
                        type="text"
                        {...register('custom_employment_status')}
                        placeholder="Please specify your employment status"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {errors.custom_employment_status && (
                        <p className="text-xs text-red-600 mt-1">{errors.custom_employment_status.message}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Move-in Details or Booking Dates */}
              {isShortlet ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Check-in Date *</label>
                    <input
                      type="date"
                      {...register('booking_start_date')}
                      value={bookingDates.start}
                      onChange={(e) => {
                        setBookingDates({ ...bookingDates, start: e.target.value })
                        setValue('booking_start_date', e.target.value)
                      }}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {'booking_start_date' in errors && errors.booking_start_date && (
                      <p className="text-xs text-red-600 mt-1">{errors.booking_start_date.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Check-out Date *</label>
                    <input
                      type="date"
                      {...register('booking_end_date')}
                      value={bookingDates.end}
                      onChange={(e) => {
                        setBookingDates({ ...bookingDates, end: e.target.value })
                        setValue('booking_end_date', e.target.value)
                      }}
                      min={bookingDates.start || new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {'booking_end_date' in errors && errors.booking_end_date && (
                      <p className="text-xs text-red-600 mt-1">{errors.booking_end_date.message}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Move-in Date *</label>
                    <input
                      type="date"
                      {...register('move_in_date')}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {'move_in_date' in errors && errors.move_in_date && (
                      <p className="text-xs text-red-600 mt-1">{errors.move_in_date.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Lease Duration</label>
                    <select {...register('lease_duration')} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="6">6 months</option>
                      <option value="12">12 months</option>
                      <option value="18">18 months</option>
                      <option value="24">24 months</option>
                    </select>
                  </div>
                </div>
              )}

              {/* KYC Documents Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Documents (Optional)</label>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Bank Statement</label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleFileUpload(file, 'bankStatement')
                      }}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Employment Letter</label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleFileUpload(file, 'employmentLetter')
                      }}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Upload additional documents to strengthen your application</p>
              </div>

              {/* Affordability Check */}
              {watch('monthly_income') && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">💡 Affordability Check</h4>
                  <div className="text-sm text-blue-700">
                    {isShortlet ? (
                      <>
                        <p>Price per Day: {formatPrice(property.price)}</p>
                        {bookingDates.start && bookingDates.end && (
                          <>
                            <p>Total Booking Cost: {formatPrice(calculateShortletTotal())}</p>
                            <p>Your Income: {formatPrice(Number(watch('monthly_income')) || 0)}/month</p>
                            <p className="text-green-600 font-medium">✅ Shortlet booking cost calculated</p>
                          </>
                        )}
                        {(!bookingDates.start || !bookingDates.end) && (
                          <p className="text-yellow-600 font-medium">⚠️ Please select check-in and check-out dates</p>
                        )}
                      </>
                    ) : (
                      <>
                        <p>Monthly Rent: {formatPrice(property.price)}</p>
                        <p>Your Income: {formatPrice(Number(watch('monthly_income')) || 0)}</p>
                        <p>Income-to-Rent Ratio: {(((Number(watch('monthly_income')) || 0) / property.price)).toFixed(1)}x</p>
                        {(((Number(watch('monthly_income')) || 0) / property.price)) >= 3 ? (
                          <p className="text-green-600 font-medium">✅ Good affordability ratio (3x or higher)</p>
                        ) : (
                          <p className="text-yellow-600 font-medium">⚠️ Consider higher income or lower rent options</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors">Cancel</button>
                <button type="submit" disabled={isSubmitting || loading} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors">
                  {isSubmitting || loading ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
        </div>
      </div>
    </div>
  )
} 

